import { ChangeEvent, DragEvent, FC, KeyboardEvent, MutableRefObject, ReactNode, useRef, useState } from "react";

import { AttachmentChip } from "./AttachmentChip";
import { FileDropZone } from "./FileDropZone";
import { IconButton } from "./IconButton";
import { AgentChatAttachment, ChatbotClassMap } from "../types";

interface Props {
  readonly styles: ChatbotClassMap;
  readonly textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
  readonly currentInputValue: string;
  readonly canSubmit: boolean;
  readonly hasError?: boolean;
  readonly attachedFiles: readonly AgentChatAttachment[];
  readonly loadingNotice?: ReactNode;
  readonly isUploadTargetVisible?: boolean;
  readonly isAttachmentEnabled?: boolean;
  readonly onAttachFile?: () => void;
  readonly onFilesSelected?: (files: FileList) => void;
  readonly onRemoveFile?: (id: string) => void;
  readonly onFileDrop?: (files: FileList) => void;
  readonly onTextareaChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  readonly onTextareaKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly onSubmit: () => void;
  readonly onRetry?: () => void;
}

/** Auto-growing input with attach/remove chips, drag-and-drop, and a send affordance. */
export const UserInput: FC<Props> = ({
  styles,
  textareaRef,
  currentInputValue,
  canSubmit,
  hasError = false,
  attachedFiles,
  loadingNotice,
  isUploadTargetVisible = false,
  isAttachmentEnabled = true,
  onAttachFile,
  onFilesSelected,
  onRemoveFile,
  onFileDrop,
  onTextareaChange,
  onTextareaKeyDown,
  onSubmit,
  onRetry,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showRetryButton = hasError && !!onRetry;
  const showSendButton = !hasError && canSubmit;
  const showUploadTarget = isAttachmentEnabled && (isDragging || isUploadTargetVisible);
  const showAttachedFiles = isAttachmentEnabled && attachedFiles.length > 0;

  const handleRetryClick = () => {
    onRetry?.();
  };

  const handleAttachClick = () => {
    onAttachFile?.();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) onFilesSelected?.(files);
    e.target.value = "";
  };

  /* The browser's default drop behaviour is to navigate away and open the dropped file, losing any
     unsent work, so dragging is always cancelled over this region. Only the handling of the files
     themselves depends on whether attachments are enabled. */
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isAttachmentEnabled) return;

    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!isAttachmentEnabled) return;

    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isAttachmentEnabled) return;

    setIsDragging(false);
    if (e.dataTransfer.files.length > 0 && onFileDrop) onFileDrop(e.dataTransfer.files);
  };

  return (
    <>
      <div
        className={styles.UserInput}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <FileDropZone styles={styles} isVisible={showUploadTarget} isDragging={isDragging} />

        {!!showAttachedFiles && (
          <div className={styles.UserInputAttachments} data-testid="attached-files">
            {attachedFiles.map((file) => (
              <AttachmentChip key={file.id} styles={styles} file={file} onRemove={onRemoveFile} />
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          name="user-prompt-input"
          className={styles.UserInputTextarea}
          placeholder="How can I help you today?"
          value={currentInputValue}
          onChange={onTextareaChange}
          onKeyDown={onTextareaKeyDown}
          rows={1}
          aria-label="Message"
          data-testid="input"
        />

        <div className={styles.UserInputControls}>
          {!!isAttachmentEnabled && (
            <>
              <IconButton
                icon="attachment"
                label="Attach file"
                tooltip="Attach file"
                onClick={handleAttachClick}
                testId="attach-file"
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                aria-hidden="true"
                tabIndex={-1}
                onChange={handleFileInputChange}
                data-testid="file-input"
              />
            </>
          )}
          <span className={styles.UserInputControlsSpacer} />
          {!!showRetryButton && (
            <IconButton
              variant="submit"
              icon="refresh"
              label="Retry"
              tooltip="Retry"
              onClick={handleRetryClick}
              testId="retry-message"
            />
          )}
          {!!showSendButton && (
            <IconButton
              variant="submit"
              icon="arrow-right"
              label="Send message"
              tooltip="Send message"
              onClick={onSubmit}
              testId="send-message"
            />
          )}
        </div>
      </div>
      {!!loadingNotice && (
        <p className={styles.UserInputNotice} role="alert" data-testid="loading-notice">
          {loadingNotice}
        </p>
      )}
    </>
  );
};
